import { ApiLinkEntity, ApiLinkRel } from "config/constants"
import i18n from "intl/i18n"
import dayjs, { Dayjs } from "utils/dayjs"
import { Guid, IApiLink } from "@/types"

export enum NotificationType {
    assetsNotShipped = "assetsNotShipped",
    assetOutOfOrder = "assetOutOfOrder",
    unplannedAssetReleased = "unplannedAssetReleased",
    unplannedAssetStored = "unplannedAssetStored",
    assetDisposed = "assetDisposed",
    storingCompleted = "storingCompleted",
    storingIncomplete = "storingIncomplete",
    splitAssetSucceeded = "splitAssetSucceeded",
    splitAssetFailed = "splitAssetFailed",
    assetReactivated = "assetReactivated",
    reactivateAssetFailed = "reactivateAssetFailed",
    releaseCompleted = "releaseCompleted",
    manualReleaseCompleted = "manualReleaseCompleted",
    manualStoreCompleted = "manualStoreCompleted",
    shippingOrderRejected = "shippingOrderRejected",
    shippingOrderChanged = "shippingOrderChanged",
    adHocReservationCreated = "adHocReservationCreated",
    reservationCanceled = "reservationCanceled",
    collectiveReferenceNumberNotUpdated = "collectiveReferenceNumberNotUpdated",
    collectiveReferenceNumberUpdated = "collectiveReferenceNumberUpdated",
    referenceNumbersUpdated = "referenceNumbersUpdated",
    assetInspected = "assetInspected",
    automaticInvoicingFailed = "automaticInvoicingFailed",
    assetMaintenanceTimeOverdueSoon = "assetMaintenanceTimeOverdueSoon",
    assetMaintenanceOperationHoursOverdueSoon = "assetMaintenanceOperationHoursOverdueSoon",
}

export enum NotificationChannel {
    sales = "sales",
    logistics = "logistics",
    finance = "finance",
}

export interface IGetNotificationDto {
    id: Guid
    notificationType: NotificationType
    date: string
    isRead: boolean
    isDone: boolean | null
    doneChangeDate?: string | null
    doneChangedBy?: Guid | null
    links?: IApiLink[]
}

export interface IGetNotification extends IGetNotificationDto {
    doneChangedByUserEmail?: string
}

export default class Notification {
    id: Guid
    notificationType: NotificationType
    date: Dayjs
    isRead: boolean
    isDone: boolean | null
    doneChangeDate?: Dayjs
    doneChangedByUserEmail?: string
    linkUpdateNotification?: IApiLink

    constructor(dto: IGetNotification) {
        this.id = dto.id
        this.notificationType = dto.notificationType
        this.date = dayjs(dto.date)
        this.isRead = dto.isRead
        this.isDone = dto.isDone
        this.doneChangeDate = dto.doneChangeDate ? dayjs(dto.doneChangeDate) : undefined
        this.doneChangedByUserEmail = dto.doneChangedByUserEmail
        this.linkUpdateNotification = dto.links?.find(lnk => lnk.entity === ApiLinkEntity.notification && lnk.rel === ApiLinkRel.update)
    }

    get canUpdate() {
        return !!this.linkUpdateNotification
    }
}

export function getNotificationChannelLabel(notificationChannel: NotificationChannel) {
    switch (notificationChannel) {
        case NotificationChannel.sales:
            return i18n.t("notificationChannel.sales")
        case NotificationChannel.logistics:
            return i18n.t("notificationChannel.logistics")
        case NotificationChannel.finance:
            return i18n.t("notificationChannel.finance")
        default:
            return ""
    }
}

export function getNotificationTypeLabel(notificationType: NotificationType, { forFilter }: { forFilter?: boolean } = {}) {
    switch (notificationType) {
        case NotificationType.assetsNotShipped:
            return i18n.t("notifications.plannedAssetsNotShipped")
        case NotificationType.assetOutOfOrder:
            return i18n.t("notifications.assetOutOfOrder")
        case NotificationType.unplannedAssetReleased:
            return i18n.t("notifications.unplannedAssetReleased.title")
        case NotificationType.unplannedAssetStored:
            return i18n.t("notifications.unplannedAssetStored.title")
        case NotificationType.assetDisposed:
            return i18n.t("notifications.assetDisposed")
        case NotificationType.storingCompleted:
            return i18n.t("notifications.storingCompleted.title")
        case NotificationType.storingIncomplete:
            return i18n.t("notifications.storingIncomplete.title")
        case NotificationType.splitAssetSucceeded:
            return i18n.t("notifications.assetSplittingSucceeded.title")
        case NotificationType.splitAssetFailed:
            return i18n.t("notifications.assetSplittingFailed.title")
        case NotificationType.assetReactivated:
            return i18n.t("notifications.assetReactivationSucceeded.title")
        case NotificationType.reactivateAssetFailed:
            return i18n.t("notifications.assetReactivationFailed.title")
        case NotificationType.releaseCompleted:
            return i18n.t("notifications.releaseCompleted.title")
        case NotificationType.manualReleaseCompleted:
            return i18n.t("notifications.manualReleaseCompleted.title")
        case NotificationType.manualStoreCompleted:
            return i18n.t("notifications.manualStoreCompleted.title")
        case NotificationType.shippingOrderRejected:
            return i18n.t("notifications.shippingOrderRejected.title")
        case NotificationType.shippingOrderChanged:
            return i18n.t("notifications.shippingOrderChanged.title")
        case NotificationType.adHocReservationCreated:
            return i18n.t("notifications.adHocReservationCreated.title")
        case NotificationType.reservationCanceled:
            return i18n.t("notifications.reservationCanceled.title")
        case NotificationType.collectiveReferenceNumberNotUpdated:
            return i18n.t("notifications.collectiveReferenceNumberNotUpdated.title")
        case NotificationType.collectiveReferenceNumberUpdated:
            return i18n.t("notifications.collectiveReferenceNumberUpdated.title")
        case NotificationType.referenceNumbersUpdated:
            return i18n.t("notifications.referenceNumbersUpdated.title")
        case NotificationType.assetInspected:
            return i18n.t("notifications.assetInspected.title")
        case NotificationType.automaticInvoicingFailed:
            return i18n.t("notifications.automaticInvoicingFailed.title")
        case NotificationType.assetMaintenanceTimeOverdueSoon:
            return forFilter
                ? i18n.t("notifications.maintenanceDueSoon.titleFilterRemainingTime")
                : i18n.t("notifications.maintenanceDueSoon.title")
        case NotificationType.assetMaintenanceOperationHoursOverdueSoon:
            return forFilter
                ? i18n.t("notifications.maintenanceDueSoon.titleFilterRemainingThreshold")
                : i18n.t("notifications.maintenanceDueSoon.title")
    }
}
